/**
 * @fileoverview Slack OAuth Callback Handler
 * Processes the OAuth callback from Slack and exchanges the code for tokens.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export function SlackCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage(error === 'access_denied' ? 'You denied access to Slack' : error);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Missing authorization code or state');
        return;
      }

      try {
        // Parse state to get tenant ID
        const stateData = JSON.parse(atob(state));
        const tenantId = stateData.tenantId;

        if (!tenantId) {
          throw new Error('Missing tenant ID in state');
        }

        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('Not authenticated');
        }

        // Exchange code for tokens via edge function
        const response = await supabase.functions.invoke('Calie_slack/oauth', {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: { code, tenantId },
        });

        if (response.error) {
          throw response.error;
        }

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'OAuth exchange failed');
        }

        setStatus('success');
        toast({
          title: 'Slack connected',
          description: 'Your Slack workspace has been connected successfully.',
        });

        // Redirect back to integrations page
        setTimeout(() => {
          navigate('/settings/integrations', { replace: true });
        }, 1500);

      } catch (err) {
        console.error('Slack OAuth error:', err);
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    handleCallback();
  }, [searchParams, navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <h2 className="text-xl font-semibold">Connecting to Slack...</h2>
            <p className="text-muted-foreground">Please wait while we complete the setup.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h2 className="text-xl font-semibold">Slack Connected!</h2>
            <p className="text-muted-foreground">Redirecting to integrations page...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-destructive" />
            <h2 className="text-xl font-semibold">Connection Failed</h2>
            <p className="text-muted-foreground">{errorMessage}</p>
            <button
              onClick={() => navigate('/settings/integrations', { replace: true })}
              className="mt-4 text-primary hover:underline"
            >
              Return to integrations
            </button>
          </>
        )}
      </div>
    </div>
  );
}
